import { matchesSearch } from './search';
import { DATA_TABLE_DEFAULT_ROWS_PER_PAGE } from './types';
import type { DataTableRowsPerPage, DataTableSortDirection, DataTableSortState } from './types';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { ReactBindings } from 'src/types/bindings';

const noop = (): void => undefined;

/** Page and rows-per-page signals; see {@link createDataTablePaging}. */
export interface DataTablePaging {
    page: Signal<number>;
    rowsPerPage: Signal<DataTableRowsPerPage>;
}

/**
 * Paging state a longer-lived model can own on behalf of a table model, so the page and page size
 * survive the table model being disposed and rebuilt (e.g. when a route remounts its panel).
 */
export function createDataTablePaging(): DataTablePaging {
    return { page: signal(1), rowsPerPage: signal<DataTableRowsPerPage>(DATA_TABLE_DEFAULT_ROWS_PER_PAGE) };
}

interface ClientPagedTableModelBaseOptions<Row, TData> {
    /** The string fields the global filter matches for a row. */
    searchableValues(row: Row): (string | null | undefined)[];
    /** Map the fetched data to rows; defaults to using the data as-is. */
    mapRows?(data: TData): readonly Row[];
    /** Externally owned paging state; the model keeps its own when omitted. */
    paging?: DataTablePaging;
    /**
     * What each sortable column orders by, by column id; other columns can't be sorted. Numbers
     * compare numerically, strings naturally, `true` before `false`, and empty values last.
     */
    sortValues?: Readonly<Record<string, (row: Row) => ClientSortValue>>;
}

export type ClientSortValue = string | number | boolean | null | undefined;

/** Order of two sort values; see {@link ClientPagedTableModelBaseOptions.sortValues}. Empty values stay last. */
export function compareSortValues(a: ClientSortValue, b: ClientSortValue, direction: DataTableSortDirection): number {
    const aEmpty = a === null || a === undefined || a === '';
    const bEmpty = b === null || b === undefined || b === '';
    if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
    const sign = direction === 'asc' ? 1 : -1;
    if (typeof a === 'number' && typeof b === 'number') return sign * (a - b);
    if (typeof a === 'boolean' && typeof b === 'boolean') return sign * (Number(b) - Number(a));
    return sign * String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Rows come from a query observer the model mounts itself. */
export interface ClientPagedTableQueryOptions<Row, TData> extends ClientPagedTableModelBaseOptions<Row, TData> {
    queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    /**
     * Query options for the row source. Pass a computed that reads scope
     * signals to re-target the query when the scope changes; scope signals
     * must be constructor locals captured by the computed, because subclass
     * fields initialize only after `super()` returns.
     */
    query: ReadonlySignal<QueryObserverOptions<TData, Error, TData>>;
}

/** Rows come from signals another model already owns (e.g. a DI model shared by a whole page). */
export interface ClientPagedTableSignalOptions<Row, TData> extends ClientPagedTableModelBaseOptions<Row, TData> {
    data: ReadonlySignal<TData | undefined>;
    isLoading?: ReadonlySignal<boolean>;
    isError?: ReadonlySignal<boolean>;
    refetch?: () => void;
}

export type ClientPagedTableModelOptions<Row, TData> =
    | ClientPagedTableQueryOptions<Row, TData>
    | ClientPagedTableSignalOptions<Row, TData>;

/**
 * Base state model for DataTable consumers with client-side paging and
 * global filtering (ADR 0007): mounts the row query and derives the
 * filtered, paginated signals the Svelte DataTable renders. Subclasses add
 * only their scope setters, extra queries (via {@link use}), and mutations.
 */
export class ClientPagedTableModel<Row, TData = readonly Row[]> implements Disposable {
    #disposableStack = new DisposableStack();
    #page: Signal<number>;
    #rowsPerPage: Signal<DataTableRowsPerPage>;
    #searchQuery = signal('');
    #refetch: () => void;
    #sort = signal<DataTableSortState | null>(null);
    readonly #sortValues: Readonly<Record<string, (row: Row) => ClientSortValue>>;

    /** All fetched rows before the global filter (e.g. for add-entry searches). */
    readonly allRows: ReadonlySignal<readonly Row[]>;
    /** Every row after the global filter and sort, across pages (e.g. for an export of what the table shows). */
    readonly filteredRows: ReadonlySignal<readonly Row[]>;
    /** The current page of rows, after the global filter. */
    readonly rows: ReadonlySignal<readonly Row[]>;
    readonly isLoading: ReadonlySignal<boolean>;
    readonly isError: ReadonlySignal<boolean>;
    /** Count after the global filter (drives the pager's "of N"). */
    readonly totalCount: ReadonlySignal<number>;
    readonly pageCount: ReadonlySignal<number>;
    /** 1-based, clamped so a shrinking result set can't strand the view past the end. */
    readonly page: ReadonlySignal<number>;
    readonly rowsPerPage: ReadonlySignal<DataTableRowsPerPage>;
    readonly searchQuery: ReadonlySignal<string>;
    /** The header sort; `null` keeps the fetched order. */
    readonly sort: ReadonlySignal<DataTableSortState | null> = computed(() => this.#sort.value);

    constructor(options: ClientPagedTableModelOptions<Row, TData>) {
        const { searchableValues, mapRows, sortValues = {} } = options;
        this.#sortValues = sortValues;
        const paging = options.paging ?? createDataTablePaging();
        this.#page = paging.page;
        this.#rowsPerPage = paging.rowsPerPage;
        let data: ReadonlySignal<TData | undefined>;
        if ('query' in options) {
            const observer = this.#disposableStack.use(
                options.queryObserverModelFactory<TData, Error, TData>(options.query),
            );
            this.#refetch = () => void observer.signal.peek().refetch();
            data = computed(() => observer.signal.value.data);
            this.isLoading = computed(() => observer.signal.value.isLoading);
            this.isError = computed(() => observer.signal.value.isError);
        } else {
            this.#refetch = options.refetch ?? noop;
            data = options.data;
            this.isLoading = options.isLoading ?? computed(() => false);
            this.isError = options.isError ?? computed(() => false);
        }

        this.allRows = computed(() => {
            const current = data.value;
            if (current === undefined) return [];
            return mapRows ? mapRows(current) : (current as readonly Row[]);
        });
        const filteredRows = computed(() => {
            const query = this.#searchQuery.value;
            const matching = this.allRows.value.filter((row) => matchesSearch(searchableValues(row), query));
            const sort = this.#sort.value;
            const value = sort ? sortValues[sort.columnId] : undefined;
            if (!sort || !value) return matching;
            return matching.toSorted((a, b) => compareSortValues(value(a), value(b), sort.direction));
        });
        this.filteredRows = filteredRows;
        this.totalCount = computed(() => filteredRows.value.length);
        this.pageCount = computed(() => {
            const rowsPerPage = this.#rowsPerPage.value;
            if (rowsPerPage === 'all') return 1;
            return Math.max(1, Math.ceil(filteredRows.value.length / rowsPerPage));
        });
        this.page = computed(() => Math.min(this.#page.value, this.pageCount.value));
        this.rowsPerPage = computed(() => this.#rowsPerPage.value);
        this.searchQuery = computed(() => this.#searchQuery.value);
        this.rows = computed(() => {
            const rowsPerPage = this.#rowsPerPage.value;
            if (rowsPerPage === 'all') return filteredRows.value;
            const start = (this.page.value - 1) * rowsPerPage;
            return filteredRows.value.slice(start, start + rowsPerPage);
        });
    }

    /** Live global filter over the table's string fields; resets paging. */
    setSearchQuery(query: string): void {
        this.#searchQuery.value = query;
        this.#page.value = 1;
    }

    /** Sorts every filtered row, not just the page; returns to the first page. */
    setSort(sort: DataTableSortState | null): void {
        this.#sort.value = sort;
        this.#page.value = 1;
    }

    canSortColumn(columnId: string): boolean {
        return columnId in this.#sortValues;
    }

    setPage(page: number): void {
        if (!Number.isFinite(page)) return;
        this.#page.value = Math.max(1, Math.trunc(page));
    }

    setRowsPerPage(rowsPerPage: DataTableRowsPerPage): void {
        if (rowsPerPage !== 'all' && (!Number.isInteger(rowsPerPage) || rowsPerPage <= 0)) return;
        this.#rowsPerPage.value = rowsPerPage;
        this.#page.value = 1;
    }

    refetch(): void {
        this.#refetch();
    }

    /** Register an extra disposable (e.g. another query observer) with the model's lifetime. */
    protected use<T extends Disposable>(disposable: T): T {
        return this.#disposableStack.use(disposable);
    }

    /** For subclass scope setters: return to page 1 and clear the filter. */
    protected resetPagingAndSearch(): void {
        this.#page.value = 1;
        this.#searchQuery.value = '';
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
